import { createSlice, createAsyncThunk, createSelector, createEntityAdapter } from "@reduxjs/toolkit";
import { EMPTY_WORDS_STATE, SPELLING_EMPTY_STATE, SPELLING_ADAPTER_SYSTEM_INITIAL_STATE } from "./initialState";
import { SPELLING_STATE_ROOT_SELECTOR } from "@app/routes/activities/spelling/constants";

export const spellingStudentsAdapter = createEntityAdapter({ sortComparer: (a, b) => a.name?.localeCompare(b.name) });

export const mapTSToSpellingStudentStatus = (ts) => ({
  id: ts?.student?.id,
  name: ts?.student?.name,

  ...SPELLING_EMPTY_STATE,
});

const studentsAdapterSelectors = spellingStudentsAdapter.getSelectors(SPELLING_STATE_ROOT_SELECTOR);

export const { selectAll: selectAllStudentsStatus, selectIds: selectStudentStatusIds, selectById: selectStudentStatusById } = studentsAdapterSelectors;

export const getBoardStateById = (boardId, state) => {
  if (state?.entities && !state?.entities?.[boardId]) {
    state.entities[boardId] = {};
  }
  return state?.entities?.[boardId];
};

const getWordsState = (state, wordId, wordObj) => {
  const id = wordId || state?.currentWordId;

  if (id === undefined) {
    return undefined;
  }
  if (!state.wordsState) {
    state.wordsState = { };
  }
  if (!state.wordsState[id]) {
    state.wordsState[id] = { ...EMPTY_WORDS_STATE, wordId: id, word: wordObj?.content, baseWord: wordObj?.base };
  }
  return state.wordsState[id];
};

const getTherapistWordsState = (state) => {
  const id = state?.therapistWordId;

  if (id === undefined) {
    return undefined;
  }
  if (!state.wordsState) {
    state.wordsState = { };
  }
  return state.wordsState?.[id] || { };
};

export const calcTotalAccuracy = (state) => {
  let total = 0;
  let success = 0;

  if (!state.wordsState) {
    return undefined;
  }

  Object.values(state.wordsState).forEach(wordState => {
    const { isSubmit, word, studentWord, mistakeData } = wordState || {};

    const isTheWordCorrect = word?.length > 0 && studentWord?.toLowerCase() === word?.toLowerCase() && mistakeData?.mistake_data?.length === 0;

    if (isSubmit) {
      total += 1;
      success += isTheWordCorrect && !(wordState?.mistakeData?.mistake_data?.length > 0) ? 1 : 0;
    }
  });

  return Math.round((100 / total) * success);
};

export const spellingSlice = createSlice({
  name: "spelling",
  initialState: { ...spellingStudentsAdapter.getInitialState(SPELLING_ADAPTER_SYSTEM_INITIAL_STATE) },
  reducers: {
    sendSpellingToWS: () => {},

    clearState: (state, action) => {
      const { boardId, uuid, wordId, word, isEsperanza, gameId, totalWords } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.requiredClearState = false;
      s.wordsState = {};
      s.accuracy = undefined;
      s.isComplete = false;
      s.isWaiting = false;
      s.measurementUUID = uuid;
      s.currentWordId = wordId;
      s.gameId = gameId;
      s.totalWords = totalWords;
      s.therapistWordId = 0;
      s.isShowCompleteDialog = false;
      s.isEsperanza = isEsperanza;
      getWordsState(s, wordId, word);
    },

    setRequiredClearState: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.requiredClearState = value;
    },
    setIsComplete: (state, action) => {
      const { boardId } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.isComplete = true;
    },
    setIsWaiting: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.isWaiting = value;
    },
    setCurrentWordId: (state, action) => {
      const { boardId, value } = action.payload;
      const { wordId, word } = value;
      const s = getBoardStateById(boardId, state);
      s.currentWordId = wordId;
      
      getWordsState(s, wordId, word);
    },
    setTherapistWordId: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.therapistWordId = value;
    },

    setShowCompleteDialog: (state, action) => {
      const { boardId } = action.payload;
      const s = getBoardStateById(boardId, state);
      s.isShowCompleteDialog = true;
    },

    setMistakeData: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);

      const wordState = getWordsState(s, value?.wordId);
      if (!wordState) { return; }
      wordState.mistakeData = value;
      wordState.isLoadingMistakesData = false;
      s.accuracy = calcTotalAccuracy(s);
    },
    addMistakeData: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getTherapistWordsState(s);
      if (!wordState) { return; }
      wordState?.mistakeData?.mistake_data && wordState.mistakeData?.mistake_data.push(value);
      s.accuracy = calcTotalAccuracy(s);
    },
    deleteMistakeData: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getTherapistWordsState(s);
      if (!wordState) { return; }
      wordState?.mistakeData?.mistake_data && wordState.mistakeData?.mistake_data.splice(value, 1);
      s.accuracy = calcTotalAccuracy(s);
    },
    updateMistakeDataConcept: (state, action) => {
      const { boardId, value } = action.payload;
      const { index, conceptId } = value;

      const s = getBoardStateById(boardId, state);
      const wordState = getTherapistWordsState(s);
      if (!wordState) { return; }
      if (wordState.mistakeData?.mistake_data?.[index]) {
        wordState.mistakeData.mistake_data[index].concept = conceptId;
      }
    },
    setCurrentStep: (state, action) => {
      const { boardId, value, wordId } = action.payload;
      const s = getBoardStateById(boardId, state);
      
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }
      wordState.currentStep = value;
    },
    setSketchTool: (state, action) => {
      const { boardId, value, wordId } = action.payload;
      const s = getBoardStateById(boardId, state);
      
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }
      wordState.coding.sketchTool = value;
    },
    setSketch: (state, action) => {
      const { boardId, value, wordId } = action.payload;
      const s = getBoardStateById(boardId, state);
      
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }
      wordState.coding.sketch = value;
    },

    resetCoding: (state, action) => {
      const { boardId, wordId } = action.payload;
      const s = getBoardStateById(boardId, state);
      
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }
      wordState.coding.sketch = {};
      wordState.coding.letters = {};
    },

    setNextWord: (state, action) => {
      const { boardId, nextWordId, nextWord } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s);
      if (!wordState) { return; }
      
      s.currentWordId = nextWordId;
      nextWordId >= 0 && getWordsState(s, nextWordId, nextWord);
    },

    setIsSubmit: (state, action) => {
      const { boardId, value, wordId } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }
      wordState.isSubmit = value;

      s.accuracy = calcTotalAccuracy(s);
    },

    setIsListen: (state, action) => {
      const { boardId, value, wordId } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }
      wordState.isListen = value;
    },
    setIsEcho: (state, action) => {
      const { boardId, value, wordId } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }
      wordState.isEcho = value;
    },
    setIsSpell: (state, action) => {
      const { boardId, value, wordId } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }
      wordState.isSpell = value;
    },
    setIsWrite: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s);
      if (!wordState) { return; }
      wordState.isWrite = value;
    },
    setIsCoding: (state, action) => {
      const { boardId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s);
      if (!wordState) { return; }
      wordState.isCoding = value;
    },

    setCodingSelectedLetters: (state, action) => {
      const { boardId, value, wordId } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }
      wordState.coding.selectedLetters = value;
    },
    
    setCodingWordColor: (state, action) => {
      const { boardId, value, wordId } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }
      
      wordState.coding.color = value;
    },

    setSketchingWordColor: (state, action) => {
      const { boardId, value, wordId } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }
      
      wordState.coding.sketchColor = value;
    },

    setCodingLetterColor: (state, action) => {
      const { boardId, value, wordId } = action.payload;
      const { letterId, newState } = value;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }

      if (!wordState.coding.letters[letterId]) {
        wordState.coding.letters[letterId] = { codes: {} };
      }
      wordState.coding.letters[letterId].color = newState;
    },

    setCodingWordCode: (state, action) => {
      const { boardId, value, wordId } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }
      wordState.coding.letters = value;
    },

    setCodingLetterCode: (state, action) => {
      const { boardId, value, wordId } = action.payload;
      const { letterId, codeId, newState } = value;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }
      if (!wordState.coding.letters[letterId]) {
        wordState.coding.letters[letterId] = { codes: {} };
      }
      if (newState) {
        wordState.coding.letters[letterId].codes[codeId] = newState;
        wordState.isCoding = true;
      } else {
        delete wordState.coding.letters[letterId].codes[codeId];
      }
    },

    updateStudentWord: (state, action) => {
      const { boardId, value, wordId } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }

      wordState.studentWord = value;
      wordState.isWrite = true;
      wordState.isLoadingMistakesData = true;
    },

    updatePreviewSpellingStatus: (state, action) => {
      state.presenting = action.payload;
    },

    setMeasurementUUID: (state, action) => {
      const { boardId, wordId, value } = action.payload;
      const s = getBoardStateById(boardId, state);
      const wordState = getWordsState(s, wordId);
      if (!wordState) { return; }
      wordState.measurementUUID = value;
    },

    updateStudentSpellingStatus: spellingStudentsAdapter.updateOne,
    setStudentsSpellingStatus: spellingStudentsAdapter.setAll,
    updateAllStudentsSpelling: spellingStudentsAdapter.updateMany,
    addOneSpellingStatus: spellingStudentsAdapter.addOne,
  },
  extraReducers: { },
});

export default spellingSlice.reducer;

//actions

export const {
  sendSpellingToWS,

  clearState,
  setRequiredClearState,
  
  setCurrentWordId,
  setTherapistWordId,
  setShowCompleteDialog,
  setCurrentStep,
  setSketchTool,
  setSketch,
  resetCoding,
  setIsComplete,
  setIsWaiting,

  setMistakeData,
  addMistakeData,
  deleteMistakeData,
  updateMistakeDataConcept,
  setNextWord,
  setIsSubmit,
  setIsListen,
  setIsEcho,
  setIsSpell,
  setIsWrite,
  setIsCoding,

  setCodingSelectedLetters,
  setCodingWordColor,
  setCodingLetterColor,
  setSketchingWordColor,
  setCodingWordCode,
  setCodingLetterCode,

  updateStudentWord,

  updateStudentSpellingStatus,
  addOneSpellingStatus,
  setStudentsSpellingStatus,
  updateAllStudentsSpelling,

  updatePreviewSpellingStatus,

  setMeasurementUUID,
} = spellingSlice.actions;

const spellingStudentsAdapterSelectors = spellingStudentsAdapter.getSelectors(SPELLING_STATE_ROOT_SELECTOR);

export const {
  selectById: spellingSelectStudentStatusById,
} = spellingStudentsAdapterSelectors;

//selectors
export const createSimpleSelector = (fieldName) => createSelector(
  [spellingSelectStudentStatusById], studentStatus => fieldName
    ? studentStatus?.[fieldName]
    : studentStatus,
);

export const createWordsStateSelector = (fieldName) => createSelector(
  [spellingSelectStudentStatusById], studentStatus => fieldName
    ? studentStatus?.wordsState?.[studentStatus.currentWordId]?.[fieldName]
    : studentStatus?.wordsState?.[studentStatus.currentWordId],
);

export const createTherapistWordStateSelector = (fieldName) => createSelector(
  [spellingSelectStudentStatusById], studentStatus => fieldName
    ? studentStatus?.wordsState?.[studentStatus.therapistWordId]?.[fieldName]
    : studentStatus?.wordsState?.[studentStatus.therapistWordId],
);

export const selectIsUpdatingMeasurements = createSimpleSelector("isUpdatingMeasurements");
export const selectIsUpdatingMeasurementsError = createSimpleSelector("isUpdatingMeasurementsError");

export const selectAnaliticsTrackEventFunc = createSimpleSelector("analiticsTrackEventFunc");
export const selectRequiredClearState = createSimpleSelector("requiredClearState");
export const selectCurrentWordId = createSimpleSelector("currentWordId");
export const selectTherapistWordId = createSimpleSelector("therapistWordId");
export const selectIsShowCompleteDialog = createSimpleSelector("isShowCompleteDialog");
export const selectIsComplete = createSimpleSelector("isComplete");
export const selectIsWaiting = createSimpleSelector("isWaiting");

export const selectTotalAccuracy = createSimpleSelector("accuracy");
export const selectWordsState = createSimpleSelector("wordsState");
export const selectIsEsperanza = createSimpleSelector("isEsperanza");

export const selectPreviewSpellingStatus = createSimpleSelector();
export const selectIsTheWordCorrect = createSelector(
  [spellingSelectStudentStatusById], studentStatus => {
    const studentWord = studentStatus?.wordsState?.[studentStatus?.therapistWordId]?.studentWord;
    const word = studentStatus?.wordsState?.[studentStatus?.therapistWordId]?.word;
    return word?.length > 0 && studentWord?.toLowerCase() === word?.toLowerCase();
  },
);

export const selectIsLoadingMistakesData = createTherapistWordStateSelector("isLoadingMistakesData");
export const selectCurrentStep = createWordsStateSelector("currentStep");
export const selectCurrentWordState = createWordsStateSelector();

export const selectIsSpell = createWordsStateSelector("isSpell");
export const selectIsEcho = createWordsStateSelector("isEcho");
export const selectIsWrite = createWordsStateSelector("isWrite");
export const selectIsCoding = createWordsStateSelector("isCoding");

export const selectTherapistWordState = createTherapistWordStateSelector();

export const setAndSendSpellingToWS = createAsyncThunk(
  "spelling/setAndSendSpellingToWS",
  async ({ reducer, data }, thunkAPI) => {
    thunkAPI.dispatch(sendSpellingToWS({ reducerType: reducer?.type, ...data }));
    reducer && thunkAPI.dispatch(reducer(data));
  },
);

const reducers_map = {
  "spelling/clearState": clearState,
  "spelling/setIsComplete": setIsComplete,
  "spelling/setCurrentWordId": setCurrentWordId,
  "spelling/setCurrentStep": setCurrentStep,
  "spelling/setNextWord": setNextWord,
  "spelling/setIsSubmit": setIsSubmit,
  "spelling/setIsListen": setIsListen,
  "spelling/setIsSpell": setIsSpell,
  "spelling/setIsEcho": setIsEcho,
  "spelling/setIsWrite": setIsWrite,
  "spelling/setIsCoding": setIsCoding,
  "spelling/setCodingSelectedLetters": setCodingSelectedLetters,
  "spelling/setCodingWordColor": setCodingWordColor,
  "spelling/setCodingWordCode": setCodingWordCode,
  "spelling/setCodingLetterColor": setCodingLetterColor,
  "spelling/setCodingLetterCode": setCodingLetterCode,
  "spelling/updateStudentWord": updateStudentWord,
  "spelling/setMistakeData": setMistakeData,
  "spelling/setSketch": setSketch,
  "spelling/setSketchTool": setSketchTool,
  "spelling/resetCoding": resetCoding,
  "spelling/setSketchingWordColor": setSketchingWordColor,
};

export const getSpellingReducerByType = (type) => {
  const reducers = reducers_map[type];
  // eslint-disable-next-line no-console
  !reducers && console.error("getSpellingReducerByType - type is missing: ", type);
  return reducers;
};
